import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useSession, useIsInternalStaff } from '@/hooks/useSession';
import {
  useLocations,
  useAccounts,
  useLocationOptions,
  useUpdateLocation,
  useDeleteLocation,
  useCreateLocation,
} from '../hooks';
import { LocationsFiltersBar } from '../components/LocationsFilters';
import { LocationsTable } from '../components/LocationsTable';
import { LocationFormModal, CreateLocationModal } from '../components/modals';
import { GpsPickerModal } from '../components/GpsPickerModal';
import { ColumnVisibilityToggle, DEFAULT_VISIBLE_COLUMNS } from '../components/ColumnVisibilityToggle';
import type { LocationColumnKey } from '../components/ColumnVisibilityToggle';
import { exportLocationsToCSV } from '../utils/csvExport';
import { exportLocationsToExcel } from '../utils/excelExport';
import type { Location, LocationsFilters as LocationsFiltersType, UpdateLocationInput, CreateLocationInput } from '../types';

const COLUMNS_STORAGE_KEY = 'atek_location_columns';

function loadVisibleColumns(): LocationColumnKey[] {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch {
    // ignore parse errors
  }
  return DEFAULT_VISIBLE_COLUMNS;
}

export default function LocationsPage() {
  const { t } = useTranslation('locations');

  // Session for admin level checks
  const { user: sessionUser } = useSession();
  const isFullAdmin = !!(sessionUser?.postgres_roles?.includes('full_admin') || sessionUser?.postgres_roles?.includes('director'));
  const isAtekUser = useIsInternalStaff();
  const canDelete = isFullAdmin;

  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [accountId, setAccountId] = useState('all');
  const [siteId, setSiteId] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<LocationsFiltersType['sortKey']>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<LocationColumnKey[]>(loadVisibleColumns);

  const handleVisibleColumnsChange = (columns: LocationColumnKey[]) => {
    setVisibleColumns(columns);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  };

  // Modal state
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [gpsPickerState, setGpsPickerState] = useState<{
    open: boolean;
    source: 'create' | 'edit';
    lat?: number;
    lng?: number;
  }>({ open: false, source: 'create' });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build filters object
  const filters: LocationsFiltersType = {
    search: debouncedSearch || undefined,
    accountId: accountId !== 'all' ? accountId : undefined,
    siteId: siteId !== 'all' ? siteId : undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  // Queries
  const locationsQuery = useLocations(filters);
  const accountsQuery = useAccounts();
  const optionsQuery = useLocationOptions(accountId !== 'all' ? accountId : undefined);

  // Mutations
  const createLocationMutation = useCreateLocation();
  const updateLocationMutation = useUpdateLocation();
  const deleteLocationMutation = useDeleteLocation();

  const locations = locationsQuery.data?.locations || [];
  const total = locationsQuery.data?.total || 0;
  const accounts = accountsQuery.data || [];
  const sites = optionsQuery.data?.sites || [];

  // Handlers
  const handleSort = (key: LocationsFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleAccountChange = (value: string) => {
    setAccountId(value);
    setSiteId('all'); // Reset site filter when account changes
    setPage(0);
  };

  const handleSiteChange = (value: string) => {
    setSiteId(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleCreateLocation = async (data: CreateLocationInput) => {
    await createLocationMutation.mutateAsync(data);
    setShowCreateModal(false);
  };

  const handleUpdateLocation = async (data: UpdateLocationInput) => {
    if (!editingLocation) return;
    // Use id (V4) or fallback to _id (V3 legacy)
    const locationId = editingLocation.id || editingLocation._id || editingLocation.mongoLocationId;
    if (!locationId) {
      toast.error('Cannot update location: missing location ID');
      return;
    }

    await updateLocationMutation.mutateAsync({
      locationId,
      data,
    });
    setEditingLocation(null);
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!locationId) {
      toast.error('Cannot delete location: missing location ID');
      return;
    }
    await deleteLocationMutation.mutateAsync(locationId);
    setEditingLocation(null);
  };

  const hasActiveFilters = Boolean(search || accountId !== 'all' || siteId !== 'all');

  const handleClearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setAccountId('all');
    setSiteId('all');
    setPage(0);
  };

  const handleOpenGpsPickerForCreate = (lat?: number, lng?: number) => {
    setGpsPickerState({ open: true, source: 'create', lat, lng });
  };

  const handleOpenGpsPickerForEdit = (lat?: number, lng?: number) => {
    setGpsPickerState({ open: true, source: 'edit', lat, lng });
  };

  const handleGpsPickerConfirm = (_lat: number, _lng: number) => {
    // GPS coordinates are passed back through the modal's onConfirm
    // The modals will handle updating their own state through the ref pattern
    // For now, we close the picker - the actual coordinate update happens
    // via the create/edit modal's own state management
    setGpsPickerState({ open: false, source: 'create' });
  };

  const handleExport = () => {
    try {
      exportLocationsToCSV(locations, 'locations');
      toast.success(`Exported ${locations.length} locations to CSV`);
    } catch {
      toast.error('Failed to export locations');
    }
  };

  const handleExportExcel = () => {
    try {
      exportLocationsToExcel(locations);
      toast.success(`Exported ${locations.length} locations to Excel`);
    } catch {
      toast.error('Failed to export locations to Excel');
    }
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            {t('page.title', 'Locations')} <small>{t('page.subtitle', 'Manage locations and rooms')}</small>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <ColumnVisibilityToggle
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={handleVisibleColumnsChange}
          />
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-1" />
            {t('toolbar.create', 'Create')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <LocationsFiltersBar
          search={search}
          onSearchChange={setSearch}
          accountId={accountId}
          onAccountChange={handleAccountChange}
          siteId={siteId}
          onSiteChange={handleSiteChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          accounts={accounts}
          sites={sites}
          isLoadingAccounts={accountsQuery.isLoading}
          isLoadingSites={optionsQuery.isLoading}
          onExport={handleExport}
          onExportExcel={handleExportExcel}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} {t('toolbar.count', 'location')}{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {locationsQuery.isLoading ? (
        <Card>
          <CardContent className="p-0">
            <div className="space-y-2 p-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : locationsQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">{t('error.loading', 'Error loading locations. Please try again.')}</p>
          </CardContent>
        </Card>
      ) : (
        <LocationsTable
          locations={locations}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onEdit={setEditingLocation}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          showAccount={isAtekUser}
          showSite={true}
          visibleColumns={visibleColumns}
        />
      )}

      {/* Edit Modal */}
      <LocationFormModal
        open={!!editingLocation}
        onOpenChange={(open) => {
          if (!open) setEditingLocation(null);
        }}
        location={editingLocation}
        sites={sites}
        isLoadingSites={optionsQuery.isLoading}
        onSubmit={handleUpdateLocation}
        onDelete={handleDeleteLocation}
        isSubmitting={updateLocationMutation.isPending}
        isDeleting={deleteLocationMutation.isPending}
        canDelete={canDelete}
        isFullAdmin={isFullAdmin}
        onOpenGpsPicker={handleOpenGpsPickerForEdit}
      />

      {/* Create Modal */}
      <CreateLocationModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSubmit={handleCreateLocation}
        isSubmitting={createLocationMutation.isPending}
        defaultAccountId={accountId !== 'all' ? accountId : undefined}
        onOpenGpsPicker={handleOpenGpsPickerForCreate}
      />

      {/* GPS Picker Modal */}
      <GpsPickerModal
        open={gpsPickerState.open}
        onOpenChange={(open) => setGpsPickerState((prev) => ({ ...prev, open }))}
        initialLat={gpsPickerState.lat}
        initialLng={gpsPickerState.lng}
        onConfirm={handleGpsPickerConfirm}
      />
    </div>
  );
}
